import type { BadgeKind } from '@shared/ui/StateBadge';

import type { EditorialState } from './types';

/**
 * The four states, their badges, and the transition each state actually offers.
 *
 * **The badge taxonomy is closed**, so the four values borrow three existing
 * kinds and the label carries what colour cannot: `neutral` for the state
 * nobody is waiting on, `warning` for the one that names outstanding work,
 * `success` for the published end, `danger` for the rejection. Colour is never
 * the sole signal — every badge prints its state in words.
 *
 * **The transition table is the catalogue's, transcribed.** Every row menu
 * carries the full set in markup and hides what does not apply, which is the
 * fix for a real defect: when a row carried only the actions valid at render
 * time, submitting a draft left the operator with a menu that could not show
 * approve or reject, because those entries had never been in the document.
 */

export const EDITORIAL_STATES: readonly EditorialState[] = [
  'AWAITING_REVIEW',
  'DRAFT',
  'PUBLISHED',
  'REJECTED',
];

export const STATE_BADGE_KIND: Readonly<Record<EditorialState, BadgeKind>> = {
  DRAFT: 'neutral',
  AWAITING_REVIEW: 'warning',
  PUBLISHED: 'success',
  REJECTED: 'danger',
};

/** Every verb a row can offer. `open` is a navigation; the rest are writes. */
export type PostAction = 'open' | 'edit' | 'submit' | 'approve' | 'reject' | 'withdraw' | 'delete';

/**
 * What each state offers, in the pack's own order.
 *
 * `withdraw` is the one transition that reaches `DRAFT` from both
 * `AWAITING_REVIEW` (sent too early) and `PUBLISHED` (withdrawn to be reworked).
 * It is **one transition under two labels**, because the two acts are not the
 * same act: from awaiting review nothing public has happened, and from published
 * it takes a live article off every reader-facing surface. See
 * `withdrawLabelFor`.
 *
 * **`PUBLISHED` offers no `edit`, and that is the contract on both sides.**
 * Catalog §13.13 says a post is editable by its writer «in draft and in
 * rejected, and in no other state», naming published as the reviewer's;
 * `backend-content` answers `409 CONTENT_EDITORIAL_NOT_EDITABLE` for the other
 * two. The entry was here, the edit route refused it with a notice, and the
 * operator met a dead end one press in. Reworking a published post is
 * `withdraw` first, which is exactly what the two-person review discipline
 * means: text that reached readers is re-reviewed before it reaches them again.
 *
 * **`delete` is offered in every state and returns from none of them.** It is
 * not a transition — the post is destroyed, the row leaves this table, and
 * there is no `restore` entry anywhere in this file because there is nothing
 * left to restore. The earlier machine parked a post in a fifth state and drew
 * a «استعد المقال» beside it; the state and the entry went together.
 */
export const STATE_ACTIONS: Readonly<Record<EditorialState, readonly PostAction[]>> = {
  DRAFT: ['open', 'edit', 'submit', 'delete'],
  AWAITING_REVIEW: ['open', 'approve', 'reject', 'withdraw', 'delete'],
  PUBLISHED: ['open', 'withdraw', 'delete'],
  REJECTED: ['open', 'edit', 'submit', 'approve', 'delete'],
};

/**
 * Whether a withdrawal from this state takes the post away from readers.
 *
 * The transition and its endpoint are one — `:withdraw` → `DRAFT` — but the
 * consequence is not: from `AWAITING_REVIEW` the post was never public and the
 * act is a recall, from `PUBLISHED` it is an unpublish. The label and the
 * confirmation both branch on this, because a menu entry reading «return it to
 * draft» is not what an operator wants to have pressed on a live article.
 */
export function withdrawUnpublishes(state: EditorialState): boolean {
  return state === 'PUBLISHED';
}

/**
 * The two decisions the granted reviewer role gates. They are **absent** for a
 * non-holder, never disabled: a disabled control promises a permission that is
 * coming, and this one is never coming to a writer.
 */
export const REVIEWER_ACTIONS: readonly PostAction[] = ['approve', 'reject'];

/** The two states whose writer may edit — draft and rejected, and no other. */
export function isWriterEditable(state: EditorialState): boolean {
  return state === 'DRAFT' || state === 'REJECTED';
}

/** The facet's chips: «الكلّ» plus one per state, in the pack's order. */
export type StateFilter = 'all' | EditorialState;

export const STATE_FILTERS: readonly StateFilter[] = ['all', ...EDITORIAL_STATES];
